import React, { memo, useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { Contact, ContactStore } from '../stores/ContactStore';
import { SortField, SortDirection } from '../../components/ColumnFilters';
import { List, RowComponentProps } from 'react-window';
import { CardFieldType } from '../../components/CardCustomizer';

interface ContactCardViewProps {
  contacts: Contact[];
  title: string;
  maxHeight?: string;
  contactStore: ContactStore;
  columnFilters?: {
    company: string;
    lastName: string;
    firstName: string;
    email: string;
  };
  sortField?: SortField | null;
  sortDirection?: SortDirection;
  customFieldOrder?: CardFieldType[];
  nameFormat?: string;
}

const ContactCardView = memo<ContactCardViewProps>(({ 
  contacts, 
  title, 
  maxHeight = "max-h-96",
  contactStore,
  columnFilters = { company: '', lastName: '', firstName: '', email: '' },
  sortField = null,
  sortDirection = 'asc',
  customFieldOrder,
  nameFormat = 'firstLast'
}) => {
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [columns, setColumns] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<any>(null);

  // Get field order from localStorage or use provided order - update when customFieldOrder changes
  const fieldOrder = useMemo<CardFieldType[]>(() => {
    if (customFieldOrder) return customFieldOrder;
    try {
      const saved = localStorage.getItem('card.customOrder');
      if (saved) return JSON.parse(saved) as CardFieldType[];
    } catch {}
    return ['name', 'title', 'company', 'email', 'phone', 'source'];
  }, [customFieldOrder]);

  // Reset list when field order changes
  useEffect(() => {
    setSelectionVersion(prev => prev + 1);
    if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
      listRef.current.resetAfterIndex(0);
    }
  }, [fieldOrder]);

  // Calculate columns based on screen width
  useLayoutEffect(() => {
    const updateColumns = () => {
      const width = containerRef.current?.clientWidth || window.innerWidth;
      if (width >= 1536) setColumns(5); // 2xl:grid-cols-5
      else if (width >= 1280) setColumns(4); // xl:grid-cols-4
      else if (width >= 1024) setColumns(3); // lg:grid-cols-3
      else if (width >= 640) setColumns(2); // sm:grid-cols-2
      else setColumns(1); // grid-cols-1
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  // Listen for selection changes to force re-render
  useEffect(() => {
    const handleSelectionChange = () => {
      setSelectionVersion(prev => prev + 1);
      if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
        listRef.current.resetAfterIndex(0);
      }
    };

    contactStore.addSelectionListener(handleSelectionChange);
    return () => {
      contactStore.removeSelectionListener(handleSelectionChange);
    };
  }, [contactStore]);

  const handleContactSelect = (contactId: string) => {
    if (!contactId) return;
    contactStore.toggleContactSelection(contactId);
  };

  // Helper function to format name based on preference
  const formatName = (contact: Contact) => {
    if (nameFormat === 'lastFirst' && contact.surname) {
      if (contact.givenName) {
        return `${contact.surname}, ${contact.givenName}`;
      }
      return contact.surname;
    }
    
    // Default to 'firstLast' format using givenName and surname
    if (contact.givenName && contact.surname) {
      return `${contact.givenName} ${contact.surname}`;
    }
    if (contact.givenName) {
      return contact.givenName;
    }
    if (contact.surname) {
      return contact.surname;
    }
    
    // Only use displayName if both givenName and surname are unavailable
    return contact.displayName;
  };

  // Helper function to render field based on type
  const renderField = (contact: Contact, fieldType: CardFieldType, isFirst: boolean) => {
    switch (fieldType) {
      case 'name':
        const formattedName = formatName(contact);
        return (
          <h4 className={`font-semibold text-gray-900 text-sm leading-tight break-words`} title={contact.displayName}>
            {formattedName}
          </h4>
        );
      case 'title':
        return contact.title ? (
          <p className={`${isFirst ? 'font-semibold' : ''} text-xs text-gray-600 leading-tight break-words line-clamp-2`} title={contact.title}>{contact.title}</p>
        ) : null;
      case 'company':
        return contact.company ? (
          <h4 className={`${isFirst ? 'font-semibold' : 'font-medium'} text-gray-900 text-sm leading-tight break-words`} title={contact.company}>{contact.company}</h4>
        ) : null;
      case 'email':
        return contact.emailPrimary ? (
          <div className="mt-1 sm:mt-1.5">
            <p className={`${isFirst ? 'font-semibold' : ''} text-xs text-blue-600 break-all line-clamp-1`} title={contact.emailPrimary}>{contact.emailPrimary}</p>
          </div>
        ) : null;
      case 'phone':
        return contact.phones.length > 0 ? (
          <div className="mt-1 sm:mt-1.5 max-h-[60px] overflow-y-auto">
            {contact.phones.map((phone, phoneIndex) => {
              // Handle both old format (string) and new format (object)
              const phoneNumber = typeof phone === 'string' ? phone : phone.number;
              const phoneType = typeof phone === 'string' ? null : phone.type;
              
              return (
                <p key={phoneIndex} className={`${isFirst ? 'font-semibold' : ''} text-xs text-gray-600 break-all`} title={phoneType ? `${phoneType === 'work' ? 'Work' : phoneType === 'mobile' ? 'Mobile' : 'Home'}: ${phoneNumber}` : phoneNumber}>
                  {phoneType && <span className="text-gray-400">{phoneType === 'work' ? 'Work' : phoneType === 'mobile' ? 'Mobile' : 'Home'}:</span>} {phoneNumber}
                </p>
              );
            })}
          </div>
        ) : null;
      case 'source':
        return (
          <div className="flex justify-end sticky bottom-0 bg-inherit">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs ${isFirst ? 'font-bold' : 'font-medium'} ${
              contact.sourceType === 'personal' 
                ? 'bg-green-100 text-green-800' 
                : contact.sourceType === 'internal'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-blue-100 text-blue-800'
            }`}>
              {contact.sourceType === 'personal' ? 'Personal' : 
               contact.sourceType === 'internal' ? 'Internal' : 'Corporate'}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  // Filter and sort contacts based on column filters and sort settings
  const filteredContacts = useMemo(() => {
    // First filter contacts based on column filters
    let filtered = contacts.filter(contact => {
      const companyMatch = !columnFilters.company || 
        (contact.company || '').toLowerCase().includes(columnFilters.company.toLowerCase());
      
      const lastNameMatch = !columnFilters.lastName || 
        (contact.surname || '').toLowerCase().includes(columnFilters.lastName.toLowerCase());
      
      const firstNameMatch = !columnFilters.firstName || 
        (contact.givenName || '').toLowerCase().includes(columnFilters.firstName.toLowerCase());
      
      const emailMatch = !columnFilters.email || 
        (contact.emailPrimary || '').toLowerCase().includes(columnFilters.email.toLowerCase());
      
      return companyMatch && lastNameMatch && firstNameMatch && emailMatch;
    });

    // Then sort if sort field is specified
    if (!sortField) return filtered;

    return [...filtered].sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortField) {
        case 'company':
          aValue = a.company || '';
          bValue = b.company || '';
          break;
        case 'firstName':
          aValue = a.givenName || '';
          bValue = b.givenName || '';
          break;
        case 'lastName':
          aValue = a.surname || '';
          bValue = b.surname || '';
          break;
        case 'email':
          aValue = a.emailPrimary || '';
          bValue = b.emailPrimary || '';
          break;
        case 'source':
          aValue = a.sourceType;
          bValue = b.sourceType;
          break;
        default:
          return 0;
      }

      // Use localeCompare with Latvian locale for proper sorting of special characters
      // Fallback to English for non-Latvian letters (Q, W, X, Y)
      const comparison = aValue.localeCompare(bValue, ['lv', 'en'], { sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [contacts, columnFilters.company, columnFilters.lastName, columnFilters.firstName, columnFilters.email, sortField, sortDirection]);

  const handleSelectAll = () => {
    const allSelected = filteredContacts.every(contact => contactStore.isContactSelected(contact.id));
    if (allSelected) {
      filteredContacts.forEach(contact => contactStore.deselectContact(contact.id));
    } else {
      filteredContacts.forEach(contact => contactStore.selectContact(contact.id));
    }
  };

  // Calculate rows and get item size
  const rowsCount = Math.ceil(filteredContacts.length / columns);
  

  const getItemSize = (index: number) => {
    // Dynamic height based on how many fields are set to 'none' in the customizer
    const noneCount = fieldOrder.filter((f) => f === 'none').length;
    const cappedNoneCount = Math.min(noneCount, 2); // cap to match requested behavior
    const height = 160 - 25 * cappedNoneCount; // 185 (all 6 fields), 160 (one none), 135 (two+ none)
    return height;
  };
  // Get row data (contacts for a specific row)
  const getRowContacts = (rowIndex: number) => {
    const startIdx = rowIndex * columns;
    return filteredContacts.slice(startIdx, startIdx + columns);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {title} ({filteredContacts.length.toLocaleString()})
          {filteredContacts.length !== contacts.length && (
            <span className="text-sm text-gray-500 ml-2">
              of {contacts.length.toLocaleString()}
            </span>
          )}
        </h3>
      </div>
      
      {/* Select All Checkbox */}
      {filteredContacts.length > 0 && (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={filteredContacts.every(contact => contactStore.isContactSelected(contact.id))}
            onChange={handleSelectAll}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label="Select all contacts"
          />
          <label className="text-sm text-gray-700">
            Select All ({filteredContacts.length} contacts)
          </label>
        </div>
      )}

      <div ref={containerRef} className="w-full h-[550px]">
        <List
          listRef={listRef}
          rowCount={rowsCount}
          rowHeight={getItemSize}
          rowComponent={({ index, style }: RowComponentProps) => {
            const rowContacts = getRowContacts(index);
            // eslint-disable-next-line react/forbid-dom-props
            return (
              <div style={style} className="flex gap-3 sm:gap-4 px-1 pb-1">
                {Array.from({ length: columns }).map((_, colIdx) => {
                  const contact = rowContacts[colIdx];
                  if (!contact?.id) return <div key={colIdx} className="flex-1" />;
                  
                  const isSelected = contactStore.isContactSelected(contact.id);
                  const isInEmail = contactStore.isContactInEmail(contact);
                  
                  return (
                    <div
                      key={contact.id}
                      className={`p-2 sm:p-3 rounded-lg border shadow-sm border-gray-200 cursor-pointer transition-colors flex-1 ${
                        isInEmail 
                          ? 'bg-gray-200 border-gray-300 opacity-60' 
                          : isSelected 
                            ? 'bg-blue-50 border-blue-300' 
                            : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => !isInEmail && handleContactSelect(contact.id)}
                    >
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isInEmail}
                          onChange={() => !isInEmail && handleContactSelect(contact.id)}
                          className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-0.5 sm:mt-1 flex-shrink-0 ${
                            isInEmail ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select ${contact.displayName}`}
                        />
                        <div className="flex-1 min-w-0 space-y-0.5 max-h-[170px] overflow-y-auto">
                          {fieldOrder.map((fieldType, index) => (
                            <React.Fragment key={index}>
                              {renderField(contact, fieldType, index === 0)}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }}
          rowProps={{}}
        />
      </div>
    </div>
  );
});

ContactCardView.displayName = 'ContactCardView';

export default ContactCardView;
